
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface MobileLinkProps {
  to: string;
  children: React.ReactNode;
  className?: string;
  onClick: () => void;
}

const MobileLink: React.FC<MobileLinkProps> = ({ to, children, className = "", onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className={`block w-full text-center text-3xl md:text-4xl font-sans-jakarta font-bold text-slate-900 hover:text-accent transition-colors py-6 border-b border-slate-100 last:border-0 ${className}`}
  >
    {children}
  </Link>
);

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Close menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrolled = window.scrollY > 20;
          setIsScrolled((prev) => (prev !== scrolled ? scrolled : prev));
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Show solid nav if scrolled OR if menu is open
  const showSolidNav = isScrolled || isMobileMenuOpen;

  const navClasses = showSolidNav
    ? 'bg-white shadow-md border-b border-slate-100 py-4'
    : 'bg-transparent py-6 md:py-8';

  // Desktop Link Styles
  const baseLinkClasses = "px-6 py-3 rounded-full text-sm font-bold uppercase tracking-widest transition-all duration-300";
  const linkTextColor = showSolidNav ? 'text-slate-600' : 'text-white/90 drop-shadow-md';
  const linkHoverClasses = "hover:bg-accent hover:text-white hover:shadow-xl hover:shadow-accent/20 hover:-translate-y-0.5 hover:drop-shadow-none";
  const linkClasses = `${baseLinkClasses} ${linkTextColor} ${linkHoverClasses}`;

  // Logo sizing and color inversion logic
  const logoHeight = showSolidNav ? 'h-14 md:h-16' : 'h-20 md:h-28';
  // Note: Assuming original logo is dark/colored. invert makes it white for dark backgrounds.
  const logoFilter = showSolidNav ? '' : 'brightness-0 invert drop-shadow-md';

  // Burger Icon Color & Shadow
  const burgerColor = showSolidNav ? 'text-slate-900' : 'text-white drop-shadow-md';

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ease-in-out ${navClasses}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-8 flex items-center justify-between">

          <Link to="/" className="flex items-center gap-3 relative z-[101] shrink-0" onClick={() => setIsMobileMenuOpen(false)}>
            <img
              src="https://ik.imagekit.io/6dlukcyok/Medical%20bucket/Gemini_Generated_Image_aaa78eaaa78eaaa7-removebg-preview.png"
              alt="Dr Du Plessis Logo"
              className={`w-auto object-contain transition-all duration-300 ${logoHeight} ${logoFilter}`}
              loading="eager"
              fetchPriority="high"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2">
            <Link to="/" className={linkClasses}>Home</Link>
            <Link to="/services" className={linkClasses}>Services</Link>
            <Link to="/team" className={linkClasses}>Practitioners</Link>
            <Link to="/about" className={linkClasses}>About Us</Link>

            <div className="pl-4">
              <a
                href="https://www.recomed.co.za/by-name/dr%20cg%20du%20plessis%20partners/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-primary text-white px-8 py-4 rounded-full text-sm font-bold uppercase tracking-widest hover:bg-slate-800 hover:scale-105 transition-all shadow-lg hover:shadow-primary/20 whitespace-nowrap block"
              >
                Book Appointment
              </a>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`lg:hidden relative z-[101] p-2 -mr-2 focus:outline-none transition-colors duration-300 ${burgerColor}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            <span className="material-symbols-outlined text-4xl md:text-5xl">
              {isMobileMenuOpen ? 'close' : 'menu'}
            </span>
          </button>

        </div>
      </nav>

      {/* Full Screen Mobile Menu Overlay - Solid Background for Readability */}
      <div
        className={`fixed inset-0 z-[90] bg-white lg:hidden flex flex-col justify-start pt-32 pb-8 overflow-y-auto px-8 transition-all duration-300 ease-in-out ${isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
          }`}
      >
        <div className="flex flex-col items-center gap-2 w-full max-w-lg mx-auto min-h-max transform transition-transform duration-500 delay-75"
          style={{ transform: isMobileMenuOpen ? 'translateY(0)' : 'translateY(20px)' }}>

          <MobileLink to="/" onClick={() => setIsMobileMenuOpen(false)}>Home</MobileLink>
          <MobileLink to="/services" onClick={() => setIsMobileMenuOpen(false)}>Services</MobileLink>
          <MobileLink to="/team" onClick={() => setIsMobileMenuOpen(false)}>Practitioners</MobileLink>
          <MobileLink to="/about" onClick={() => setIsMobileMenuOpen(false)}>About Us</MobileLink>

          <div className="mt-12 w-full">
            <a
              href="https://www.recomed.co.za/by-name/dr%20cg%20du%20plessis%20partners/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsMobileMenuOpen(false)}
              className="w-full bg-primary text-white text-lg px-8 py-6 rounded-2xl font-bold uppercase tracking-widest hover:bg-slate-800 transition-all shadow-xl text-center block"
            >
              Book Appointment
            </a>
          </div>

          <div className="mt-12 text-center text-slate-400">
            <p className="text-xs uppercase tracking-[0.2em] font-bold mb-2">Contact Us</p>
            <a href="tel:0117052197" className="text-xl font-bold text-slate-900 hover:text-accent transition-colors">011 705 2197</a>
          </div>

        </div>
      </div>
    </>
  );
};
